import type { APIRequest, APIResponsePromise } from '@/_types/api';
import { fileUpload } from '@/_api/client-upload';
import { _callBackend } from '@/_api/api';
import { User } from '@/_types/models/user';
import { Company } from '@/_types/models/company';
import { TCountryCode } from 'countries-list';

export const getIndividualImportTemplateLink = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/advisors/download_individual_import_template',
    'POST',
    null,
    token,
  );

export const getBusinessImportTemplateLink = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/advisors/download_business_import_template',
    'POST',
    null,
    token,
  );

export const getAllClients = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend('/firms/all_clients', 'GET', null, token);

export const deleteClient = async ({
  token,
  id,
  deleteAllPrimaryUserEntities = true,
}: APIRequest<{
  id: number;
  deleteAllPrimaryUserEntities?: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/users/delete_client_from_api',
    'POST',
    { id, delete_all_primary_user_entities: deleteAllPrimaryUserEntities },
    token,
  );

export const uploadClientsList = async ({
  file,
  assignedFirmUserId,
  comment,
  documentName,
  documentType,
  taxSoftware,
  year,
  token,
}: APIRequest<{
  file: File;
  assignedFirmUserId?: number | string;
  comment?: string;
  documentName: string;
  documentType: string;
  taxSoftware?: string;
  year: string;
}>): APIResponsePromise => {
  // Get a temporary upload link
  const { upload_link, id } = await _callBackend(
    '/integrations/get_temporary_upload_link_for_user_import_document',
    'GET',
    null,
    token,
  );

  // Upload file
  const url = new URL(upload_link);
  const isDropbox: boolean = url.hostname.includes('dropbox');
  await fileUpload(upload_link, isDropbox, file);

  return await _callBackend(
    '/integrations/upload_user_import_document',
    'POST',
    {
      id,
      assigned_firm_user_id: assignedFirmUserId,
      comment,
      document_name: documentName,
      document_type: documentType,
      tax_software: taxSoftware,
      year,
    },
    token,
  );
};

export const uploadDrakeBackup = async ({
  file,
  documentName,
  token,
  onProgress,
}: APIRequest<{
  file: File;
  documentName: string;
  token?: string;
  onProgress?: (uploadedBytes: number, totalBytes: number) => void;
}>): APIResponsePromise => {
  // Initialize multipart upload (server returns { upload_id, parts, key })
  const TARGET_CHUNK_SIZE = 32 * 1024 * 1024; // 32 MB
  const desiredParts = Math.max(1, Math.ceil(file.size / TARGET_CHUNK_SIZE));

  const init = await _callBackend(
    `/pyd_drake/temporary_upload_link?filename=${encodeURIComponent(documentName)}&parts_count=${desiredParts}`,
    'GET',
    null,
    token,
  );

  // If single URL is returned, do a simple upload instead of multipart
  if (init?.upload_link) {
    await fileUpload(init.upload_link, false, file, init.headers);
    return Promise.resolve({});
  }

  const { upload_id, parts, key } = init;

  if (!upload_id || !Array.isArray(parts) || !key) {
    throw new Error('Invalid multipart init response');
  }

  // Determine chunk size targeting desired parts while respecting S3 min part size
  const MIN_CHUNK = 5 * 1024 * 1024; // 5 MB
  const chunkSize = Math.max(Math.ceil(file.size / desiredParts), MIN_CHUNK);

  const totalParts = Math.ceil(file.size / chunkSize);
  const usableParts = parts.slice(0, totalParts);

  // Upload parts sequentially to control memory/parallelism and capture ETags
  const completedParts: Array<{ etag: string; part_number: number }> = [];

  let uploadedBytes = 0;
  for (let i = 0; i < usableParts.length; i++) {
    const partNumber =
      usableParts[i].part_number || usableParts[i].partNumber || i + 1;
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);

    // PUT to presigned part URL
    const response = await fetch(usableParts[i].url, {
      method: 'PUT',
      body: blob,
    });

    if (!response.ok) {
      // Attempt to abort on failure
      try {
        await _callBackend(
          '/pyd_drake/abort_multipart_upload',
          'POST',
          { key, upload_id },
          token,
        );
      } catch {}
      throw new Error(`Failed uploading part ${partNumber}`);
    }

    const etag =
      response.headers.get('ETag') || response.headers.get('Etag') || '';
    if (!etag) {
      throw new Error(`Missing ETag for part ${partNumber}`);
    }
    completedParts.push({
      etag: etag.replaceAll('"', ''),
      part_number: partNumber,
    });

    uploadedBytes += blob.size;
    if (onProgress) onProgress(uploadedBytes, file.size);
  }

  // Complete multipart upload
  return await _callBackend(
    '/pyd_drake/complete_multipart_upload',
    'POST',
    { key, upload_id, parts: completedParts },
    token,
  );
};

export interface CreateClientAttrs {
  assignedAdvisorId?: number;
  businessEmail?: string;
  city: string;
  companyName: string;
  country: TCountryCode | '';
  email: string;
  externalEntityId: string | null;
  externalUserId: string | null;
  firmGroupId: string;
  fname: string;
  forTesting: boolean;
  lname: string;
  otherEmail: string;
  phoneNumber: string;
  preferredContactMethod: string;
  preferredName: string;
  taxReturnId: string;
  state: string;
  street: string;
  taxSoftware: string;
  timeZone: string;
  entityType: string;
  workPhoneExtenstion: string;
  workPhoneNumber: string;
  zip: string;
  dropLinkUploadId?: number;
}

export const createClient = async ({
  assignedAdvisorId,
  businessEmail,
  city,
  companyName,
  country,
  email,
  externalEntityId,
  externalUserId,
  firmGroupId,
  fname,
  forTesting,
  lname,
  otherEmail,
  phoneNumber,
  preferredContactMethod,
  preferredName,
  taxReturnId,
  state,
  street,
  taxSoftware,
  timeZone,
  entityType,
  workPhoneExtenstion,
  workPhoneNumber,
  zip,
  token,
  dropLinkUploadId,
}: APIRequest<CreateClientAttrs>): APIResponsePromise<{
  company: Company;
  company_id: number;
  user: User;
  user_id: number;
}> => {
  return await _callBackend(
    '/users/create_client_from_api',
    'POST',
    {
      assigned_advisor_id: assignedAdvisorId,
      business_email: businessEmail,
      city,
      company_name: companyName,
      country,
      email: !!email ? email : null,
      entity_type: entityType,
      externalEntityId,
      external_entity_id: externalEntityId,
      external_user_id: externalUserId,
      firm_group_id: firmGroupId,
      fname,
      for_testing: forTesting,
      generate_temp_email: !email,
      lname,
      other_email: otherEmail,
      phone_number: phoneNumber,
      preferred_contact_method: preferredContactMethod,
      preferred_name: preferredName,
      tax_return_id: taxReturnId,
      state,
      street,
      tax_software: taxSoftware,
      time_zone: timeZone || 'Pacific Time (US & Canada)',
      work_phone_extension: workPhoneExtenstion,
      work_phone_number: workPhoneNumber,
      zip,
      drop_link_upload_id: dropLinkUploadId,
    },
    token,
  );
};

export const updateAssignments = async ({
  token,
  clientIds,
  advisorId,
}: APIRequest<{
  clientIds: Array<number>;
  advisorId: number;
}>): APIResponsePromise<{ message: string }> =>
  await _callBackend(
    '/advisors/update_assignments',
    'POST',
    {
      advisor_id: advisorId,
      ids: clientIds,
    },
    token,
  );

export const addSpouse = async ({
  params,
  companyId,
  token,
}: APIRequest<{
  companyId: number;
  params: any;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/spouse`,
    'POST',
    { ...params },
    token,
  );

export const getSpouse = async ({
  companyId,
  token,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(`/companies/${companyId}/spouse`, 'GET', null, token);

export const findUserByEmail = async ({
  email,
  token,
}: APIRequest<{
  email: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/users/find_user_by_email?email=${email}`,
    'GET',
    null,
    token,
  );
